<template>
  <TabbedBox :loading="loading">
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-accounts-settings">
        <i class="far fa-cogs"></i> Accounts Settings
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-accounts-settings">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.AccountsPermissionTypes.accountConfig) && !accountsSettings.editing" class="float-right">
              <DropdownMenuItem @click="accountsSettings.editing = true" icon="fa-pencil">Edit Accounts Settings</DropdownMenuItem>
            </ActionDropMenu>
            <ContentTitle>Accounts Settings</ContentTitle>
            <ContentSubTitle>General settings for the Accounts module</ContentSubTitle>
          </div>
        </div>
        <FormGroup :labelColumnSize="6" :form="accountsSettings.form" v-show="!loading">
          <FormAccountSetting label="Fee Adjustments Stock Item" helpDefault="None">
            <template v-slot:helpContent>
              <p>This stock item will be used for adjustments in invoices. For example, invoiced NCR orders with adjustments.</p>
            </template>
            <template v-slot:entry>
              <LinkStock v-if="!accountsSettings.editing" :id="getConfigSettingInt(AccountSettingKeys.accountsAdjustmentAmountStockId)" :name="getLookupName(generalStockListLookup, getConfigSettingInt(AccountSettingKeys.accountsAdjustmentAmountStockId))" />
              <FormInputStockCode v-if="accountsSettings.editing" :value="getConfigSettingInt(AccountSettingKeys.accountsAdjustmentAmountStockId)" @input="setConfigSetting(AccountSettingKeys.accountsAdjustmentAmountStockId, $event)" />
            </template>
          </FormAccountSetting>

          <FormAccountSetting label="Rounding Adjustments - Stock Item" helpDefault="None">
            <template v-slot:helpContent>
              <p>This stock item will be used for rounding adjustments in invoices. For example, when orders have multiple invoices due to despatch and rounding errors occur.</p>
              <p>If not set, no rounding adjustment will be added to invoices</p>
              <p>See rounding adjustment maximum amount for more details</p>
            </template>
            <template v-slot:entry>
              <LinkStock v-if="!accountsSettings.editing" :id="getConfigSettingInt(AccountSettingKeys.accountsRoundingAdjustmentStockId)" :name="getLookupName(generalStockListLookup, getConfigSettingInt(AccountSettingKeys.accountsRoundingAdjustmentStockId))" />
              <FormInputStockCode v-if="accountsSettings.editing" :value="getConfigSettingInt(AccountSettingKeys.accountsRoundingAdjustmentStockId)" @input="setConfigSetting(AccountSettingKeys.accountsRoundingAdjustmentStockId, $event)" />
            </template>
          </FormAccountSetting>

          <FormAccountSetting label="Rounding Adjustments - Maximum Amount" :helpDefault="formatCurrency(0.05)">
            <template v-slot:helpContent>
              <p>This stock item will be used for rounding adjustments in invoices. For example, when orders have multiple invoices due to despatch and rounding errors occur.</p>
              <p>If not set, no rounding adjustment will be added to invoices</p>
              <p>See rounding adjustment maximum amount for more details</p>
            </template>
            <template v-slot:entry>
              <span v-if="!accountsSettings.editing">{{getConfigSettingNumber(AccountSettingKeys.accountsRoundingMaximumAmount) | formatCurrency}}</span>
              <FormInputCurrency v-if="accountsSettings.editing" :value="getConfigSettingNumber(AccountSettingKeys.accountsRoundingMaximumAmount)" @input="setConfigSetting(AccountSettingKeys.accountsRoundingMaximumAmount, $event)" />
            </template>
          </FormAccountSetting>

          <FormAccountSetting label="Rounding Adjustments - Tax Rate" helpDefault="None">
            <template v-slot:helpContent>
              <p>The tax rate applied to rounding adjustment line items in invoices</p>
            </template>
            <template v-slot:entry>
              <span v-if="!accountsSettings.editing">{{getConfigSettingInt(AccountSettingKeys.accountsRoundingTaxRateId) | formatLookupName(taxRateListLookup)}}</span>
              <FormSelect v-if="accountsSettings.editing" :options="taxRateListLookup" :value="getConfigSettingInt(AccountSettingKeys.accountsRoundingTaxRateId)" @input="setConfigSetting(AccountSettingKeys.accountsRoundingTaxRateId, $event)" />
            </template>
          </FormAccountSetting>
          
          <FormSubmit v-if="accountsSettings.editing" label="Update" :submitting="accountsSettings.submitting" @submitted="submitAccountsConfigSettings" @cancelled="cancelAccountsConfigSettings" />
        </FormGroup>
      </TabPage>

    </template>
  </TabbedBox>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import TabbedBox from "@/components/layout/tabbedBox.vue"
import Tab from "@/components/layout/tab.vue"
import TabPage from "@/components/layout/tabPage.vue"

import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'

import { AccountSettingKeys, PricingSystemTypeLookup } from '@/store/types'

export default {
  name: 'AccountConfiguration',
  mixins: [form],
  components: {
    TabbedBox,
    Tab,
    TabPage,
    FormInputStockCode
  },
  data: () => {
    return {
      loading: false,
      loadingError: '',
      taxRateList: [],
      stockList: [],
      accountData: {},

      accountsSettings: {
        editing: false,
        submitting: false,
        form: {},
        settings: {}
      }
    }
  },
  created() {
    this.AccountSettingKeys = AccountSettingKeys
    this.PricingSystemTypeLookup = PricingSystemTypeLookup
  },
  computed: {
    generalStockListLookup() {
      return this.$data.stockList.map(stock => {
        return {
          value: stock.id,
          name: `${stock.code} | ${stock.name}`
        }
      })
    },
    taxRateListLookup() {
      return this.$data.taxRateList.map(taxRate => {
        return {
          value: taxRate.id,
          name: taxRate.name
        }
      })
    },
    isPricingSystemSettingAvailable() {
      return this.accountHasProductFeature(this.AccountProductFeatureTypes.pricingAdvanced)
    }
  },
  mounted() {
    this.requestData()
  },
  methods: {
    reloadData() {
      this.requestData()
    },
    requestData() {
      this.loadingError = false
      this.loading = true

      Promise.all([
        this.$apiService.accountsService.getConfigSettings(),
        this.$apiService.adminService.getTaxRateList(),
        this.$apiService.inventoryService.getStockList(),
      ])
      .then(results => {
        this.$data.accountsSettings.settings = results[0].data
        this.$data.taxRateList = results[1].data
        this.$data.stockList = results[2].data
      })
      .catch(err => {
        this.loadingError = 'Failed to load data'
      })
      .finally(() => {
        this.loading = false
      })
    },

    // General Config Settings
    getConfigSetting(settingKey, defaultValue = '') {
      if(this.accountsSettings.settings[settingKey] !== undefined)
        return this.accountsSettings.settings[settingKey]
      return defaultValue
    },
    getConfigSettingBoolean(settingKey, defaultValue = false) {
      if(this.accountsSettings.settings[settingKey] !== undefined)
        return this.parseBoolean(this.accountsSettings.settings[settingKey])
      return defaultValue
    },
    getConfigSettingInt(settingKey, defaultValue = 0) {
      if(this.accountsSettings.settings[settingKey] !== undefined)
        return parseInt(this.accountsSettings.settings[settingKey])
      return defaultValue
    },
    getConfigSettingNumber(settingKey, defaultValue = 0) {
      if(this.accountsSettings.settings[settingKey] !== undefined)
        return this.parseDecimal(this.accountsSettings.settings[settingKey])
      return defaultValue
    },
    setConfigSetting(settingKey, value) {
      this.accountsSettings.settings[settingKey] = value
    },

    submitAccountsConfigSettings() {
      this.$data.accountsSettings.submitting = true
      this.$apiService.accountsService.updateConfigSettings({ settings: this.$data.accountsSettings.settings })
      .then(response => {
        this.$data.accountsSettings.editing = false
        this.successToast('Account Config Settings updated')
      })
      .catch(err => {
        this.$data.accountsSettings.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.accountsSettings.submitting = false
      })
    },
    cancelAccountsConfigSettings() {
      this.$data.accountsSettings.editing = false
      this.$apiService.accountsService.getConfigSettings()
      .then(response => {
        this.$data.accountsSettings.settings = response.data
      })
    },

  }
}

</script>